<template>
    <div class="mobile-form-container">
        <branding-header />

        <h3 class="form-title primary--text mt-2">Need to Update Your Order?</h3>
        <p class="text-center font-weight-light mb-6">
            Use the form below to request changes
        </p>

        <v-form
            ref="changeForm"
            class="d-flex flex-column gap-4"
            v-model="form_valid"
            @submit.prevent="submitRequest"
        >
            <div>
                <p class="mb-1 font-weight-medium">Order Reference</p>
                <p class="mb-0 font-weight-light">{{ change_request.order_reference }}</p>
            </div>

            <rc-base-input
                v-model="change_request.first_name"
                label="First Name"
                class="mb-0"
                outlined
                dense
                required
            />

            <rc-base-input
                v-model="change_request.last_name"
                label="Last Name"
                class="mb-0"
                outlined
                dense
                required
            />

            <rc-base-input
                v-model="change_request.email"
                label="Email Address"
                class="mb-0"
                type="email"
                outlined
                dense
                required
            />

            <rc-base-input
                v-model="change_request.phone"
                label="Phone Number"
                class="mb-0"
                type="tel"
                outlined
                dense
                required
            />

            <div>
                <p class="mb-1 font-weight-medium">
                    Billing & Shipping Address
                </p>
                <p class="mb-0 font-weight-light">{{ order_data.shipping_address.line1 }}</p>
                <p v-if="order_data.shipping_address.line2" class="mb-0 font-weight-light">{{ order_data.shipping_address.line2 }}</p>
                <p class="mb-0 font-weight-light">
                    {{ order_data.shipping_address.city }}, {{ order_data.shipping_address.state }} {{ order_data.shipping_address.zip }}
                </p>
            </div>

            <div class="d-flex flex-column">
                <label class="font-weight-medium mb-1" for="notes">Additional Notes</label>
                <textarea id="notes" v-model="change_request.description" rows="2" placeholder="Describe any additional changes you'd like to request for your order"></textarea>
                <transition name="fade">
                    <p
                        v-if="show_changes_required_error"
                        class="changes-error-message mt-1 mb-0"
                    >
                        Please either update one of the contact fields above or provide details in Additional Notes
                    </p>
                </transition>
            </div>

            <div class="text-center mt-3">
                <v-btn
                    class="check-all-btn"
                    color="primary"
                    text
                    small
                    @click="checkAll"
                >
                    <v-icon
                        aria-hidden="true"
                        left
                        small
                    >mdi-check-all</v-icon>
                    Check All
                </v-btn>
            </div>

            <v-checkbox
                v-model="confirmations.no_guarantees"
                class="mobile-checkbox"
                color="primary"
                :rules="[rules.required]"
                hide-details="auto"
                dense
            >
                <template #label>
                    <span class="checkbox-label">
                        I understand that we cannot guarantee that all change requests can be accommodated. We'll do our best to help!
                    </span>
                </template>
            </v-checkbox>

            <v-checkbox
                v-model="confirmations.email_consent"
                class="mobile-checkbox consent-checkbox"
                color="primary"
                :rules="[rules.required]"
                hide-details="auto"
                dense
            >
                <template #label>
                    <span class="checkbox-label">
                        I give Barrel Creek Vineyards or third-parties acting on its behalf permission to email me with order updates, info and offers.
                        Use is subject to <a
                            href="#"
                            @click.prevent.stop="showTerms"
                        >Terms</a> with an arbitration clause and a <a
                            href="#"
                            @click.prevent.stop="showPrivacy"
                        >Privacy Policy</a>.
                    </span>
                </template>
            </v-checkbox>

            <v-checkbox
                v-model="confirmations.text_consent"
                class="mobile-checkbox consent-checkbox mb-4"
                color="primary"
                :rules="[rules.required]"
                hide-details="auto"
                dense
            >
                <template #label>
                    <span class="checkbox-label">
                        I give Barrel Creek Vineyards or third-parties acting on its behalf permission to send text messages
                        with info, promotions and offers to the number above, including use of an automated
                        system. Consent is not a condition of purchase. Texts are recorded. Message and/or data
                        rates may apply. Use is subject to <a
                            href="#"
                            @click.prevent.stop="showTerms"
                        >Terms</a> with an arbitration clause and a <a
                            href="#"
                            @click.prevent.stop="showPrivacy"
                        >Privacy Policy</a>.
                    </span>
                </template>
            </v-checkbox>

            <transition name="fade">
                <v-alert
                    v-if="show_submit_error"
                    type="error"
                    dense
                    class="mb-0"
                >
                    Use the "Check All" button above or ensure all required boxes are checked before submitting.
                </v-alert>
            </transition>

            <div class="mobile-actions">
                <v-btn
                    color="primary"
                    class="action-btn"
                    outlined
                    block
                    @click="$emit('back-to-confirmation')"
                >
                    <v-icon left>mdi-keyboard-backspace</v-icon>
                    <strong>Back to Confirmation</strong>
                </v-btn>

                <v-btn
                    color="primary"
                    :loading="submitting"
                    block
                    @click="submitRequest"
                    class="action-btn submit-btn"
                >
                    <v-icon left>mdi-send</v-icon>
                    <strong>
                        {{ submitting ? 'Sending Request...' : 'Submit Change Request' }}
                    </strong>
                </v-btn>
            </div>
        </v-form>

        <placeholder-dialog ref="placeholderDialog" />
    </div>
</template>

<script>
import BrandingHeader from '@/components/demos/shared/branding-header.vue';
import PlaceholderDialog from '@/components/demos/shared/placeholder-dialog.vue';

export default {
    name: 'ChangeRequestForm',

    components: {
        BrandingHeader,
        PlaceholderDialog
    },

    props: {
        order_data: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            form_valid: false,
            submitting: false,
            show_submit_error: false,
            show_changes_required_error: false,
            confirmations: {
                no_guarantees: false,
                email_consent: false,
                text_consent: false,
            },
            change_request: {
                first_name: this.order_data.first_name,
                last_name: this.order_data.last_name,
                email: this.order_data.email,
                phone: this.order_data.phone,
                order_reference: this.order_data.order_number,
                description: `This order is a gift. Please do not include an invoice in the package`
            },
            original_values: {
                first_name: this.order_data.first_name,
                last_name: this.order_data.last_name,
                email: this.order_data.email,
                phone: this.order_data.phone
            },
            rules: {
                required: value => !!value || 'This confirmation is required'
            }
        }
    },

    computed: {
        field_changes() {
            const changes = [];

            const current_first = (this.change_request.first_name || '').trim();
            const original_first = (this.original_values.first_name || '').trim();
            if (current_first !== original_first) {
                changes.push(`• First Name changed from "${original_first}" to "${current_first}"`);
            }

            const current_last = (this.change_request.last_name || '').trim();
            const original_last = (this.original_values.last_name || '').trim();
            if (current_last !== original_last) {
                changes.push(`• Last Name changed from "${original_last}" to "${current_last}"`);
            }

            const current_email = (this.change_request.email || '').trim();
            const original_email = (this.original_values.email || '').trim();
            if (current_email !== original_email) {
                changes.push(`• Email Address changed from "${original_email}" to "${current_email}"`);
            }

            const current_phone = (this.change_request.phone || '').trim();
            const original_phone = (this.original_values.phone || '').trim();
            if (current_phone !== original_phone) {
                changes.push(`• Phone Number changed from "${original_phone}" to "${current_phone}"`);
            }

            return changes;
        },

        final_message_text() {
            const parts = [];

            if (this.field_changes.length > 0) {
                parts.push(this.field_changes.join('\n'));
            }

            if (this.change_request.description?.trim()) {
                if (parts.length > 0) {
                    parts.push('\n---\nAdditional notes:\n');
                }
                parts.push(this.change_request.description.trim());
            }

            return parts.join('');
        },

        has_any_changes() {
            return this.field_changes.length > 0 || !!this.change_request.description?.trim();
        }
    },

    watch: {
        change_request: {
            handler(val) {
                this.show_changes_required_error = false;
                this.$emit('change-request-updated', {
                    ...val,
                    final_message: this.final_message_text
                });
            },
            deep: true,
            immediate: true
        }
    },

    methods: {
        async submitRequest() {
            if (!this.has_any_changes) {
                this.show_changes_required_error = true;
                return;
            }

            if (!this.form_valid) {
                this.show_submit_error = true;
                return;
            }

            this.show_changes_required_error = false;
            this.show_submit_error = false;
            this.submitting = true;
            await new Promise(resolve => setTimeout(resolve, 1000));
            this.submitting = false;

            this.$emit('submitted', {
                type: 'change_request',
                data: this.change_request
            });
        },

        showTerms() {
            this.$refs.placeholderDialog.show('Terms & Conditions');
        },

        showPrivacy() {
            this.$refs.placeholderDialog.show('Privacy Policy');
        },

        checkAll() {
            Object.keys(this.confirmations).forEach(key => {
                this.confirmations[key] = true;
            });

            this.show_submit_error = false;
        },
    },
}
</script>

<style lang="scss" scoped>
.info-section {
    background: #f8f9fa;
    border-radius: 0.5rem;
    padding: 0.75rem;
    margin-bottom: 0.75rem;
    border: 1px solid #e9ecef;

    .section-title {
        display: flex;
        align-items: center;
        font-size: 0.9rem;
        font-weight: 600;
        margin-bottom: 0.75rem;
        color: var(--v-primary-base);
        gap: 0.25rem;
    }
}

.check-all-btn {
    width: 100%;
    background-color: var(--v-secondary-base);
}

// matching rc-base-input styles
textarea {
    display: block;
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: 1px solid #c9c9c9;
    border-radius: 0.25rem;
    font-size: 1rem;
    line-height: 1.2;
    background-color: #ffffff;
    color: #374151;
    resize: none;

    &:focus {
        outline: none;
        border-color: var(--dark-grey);
        box-shadow: 0 0 0 3px rgba(53, 53, 53, 0.1);
    }
}

.changes-error-message {
    color: #d32f2f;
    font-size: 0.75rem;
}
</style>
